import { sqDurationStore } from '@/core/core.stores';
import { parseDuration } from '@/datetime/dateTime.utilities';
import { toRangeExport } from '@/trendData/durationRange.utilities';
import { AUTO_UPDATE } from '@/trendData/trendData.constants';
import type { DurationDataForCursors } from '@/annotation/interactiveContent.types';
import type { AutoUpdateData, AutoUpdateState, RangeExport } from '@/trendData/duration.types';

/**
 * Adds the auto update intervals, which follow from the mode, manual interval, chart width, and display range
 * duration. In AUTO mode the interval is chosen so that each update moves the chart by about one pixel.
 *
 * @param autoUpdate - Auto update state from the duration store
 * @param displayDuration - Display range duration in milliseconds
 */
export function withAutoUpdateIntervals(autoUpdate: AutoUpdateState, displayDuration: number): AutoUpdateData {
  const { mode, displayPixels, manualInterval } = autoUpdate;

  let autoInterval: number;
  if (displayPixels && displayDuration) {
    autoInterval = Math.round(Math.max(AUTO_UPDATE.MIN_INTERVAL, displayDuration / displayPixels));
  } else {
    const defaultIntervalDuration = parseDuration(
      `${AUTO_UPDATE.DEFAULT_INTERVAL.value}${AUTO_UPDATE.DEFAULT_INTERVAL.units}`,
    );
    autoInterval = Number(defaultIntervalDuration?.valueOf() ?? AUTO_UPDATE.MIN_INTERVAL);
  }

  let interval: number;
  if (mode === AUTO_UPDATE.MODES.AUTO) {
    interval = autoInterval;
  } else if (mode === AUTO_UPDATE.MODES.MANUAL) {
    const manualDuration = parseDuration(`${manualInterval.value}${manualInterval.units}`);
    interval = Number(manualDuration?.valueOf() ?? AUTO_UPDATE.MIN_INTERVAL);
  } else {
    interval = AUTO_UPDATE.MIN_INTERVAL;
  }

  return { ...autoUpdate, interval, autoInterval };
}

/** The display range and auto update settings that the chart and its cursors are drawn from. */
export function buildChartDurationData(autoUpdate: AutoUpdateState, displayRange: RangeExport): DurationDataForCursors {
  return {
    displayRange,
    autoUpdate: withAutoUpdateIntervals(autoUpdate, displayRange.duration.asMilliseconds()),
  };
}

/**
 * The current display range as UTC moments, for code outside React. Builds new moments on every call, so
 * callers may change them without affecting the store or other callers.
 *
 * @deprecated Reads the legacy singleton. Use the duration store's useDisplayRange() hook instead.
 */
export function getDurationDisplayRange(): RangeExport {
  const displayRange = sqDurationStore.getState().displayRange;
  return toRangeExport(displayRange.start, displayRange.end);
}

/**
 * The current investigate range as UTC moments, for code outside React. Builds new moments on every call, so
 * callers may change them without affecting the store or other callers.
 *
 * @deprecated Reads the legacy singleton. Use the duration store's useInvestigateRange() hook instead.
 */
export function getDurationInvestigateRange(): RangeExport {
  const investigateRange = sqDurationStore.getState().investigateRange;
  return toRangeExport(investigateRange.start, investigateRange.end);
}
